import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader, Label2, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ShieldCheck, SealCheck, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Audit() {
  const [entries, setEntries] = useState(null);
  const [verify, setVerify] = useState(null);

  const load = useCallback(() => { api.get("/audit?limit=200").then((r) => setEntries(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const runVerify = async () => {
    const { data } = await api.get("/audit/verify");
    setVerify(data);
    if (data.valid) toast.success("Integrität bestätigt", { description: `${data.count} Einträge · Hash-Chain intakt` });
    else toast.error("Integritätsfehler", { description: `Bruch bei Sequenz #${data.broken_at_seq}` });
  };

  if (!entries) return <div className="space-y-2">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-12 rounded-md" />)}</div>;

  return (
    <div>
      <PageHeader title="Audit-Trail" subtitle="Revisionssicher · append-only · SHA-256 Hash-Chain (WORM)">
        <Button data-testid="verify-audit-button" variant="outline" className="gap-2" onClick={runVerify}>
          <ShieldCheck size={18} weight="bold" />Integrität prüfen
        </Button>
      </PageHeader>

      {verify && (
        <Card data-testid="verify-result" className={`mb-4 flex items-center gap-3 rounded-md border p-4 ${verify.valid ? "border-success/40 bg-success/10" : "border-danger/40 bg-danger/10"}`}>
          {verify.valid ? <SealCheck size={22} className="text-success" weight="fill" /> : <Warning size={22} className="text-danger" weight="fill" />}
          <span className="text-sm font-semibold">{verify.valid ? `Hash-Chain intakt · ${verify.count} Einträge verifiziert` : `Manipulation erkannt bei #${verify.broken_at_seq}`}</span>
        </Card>
      )}

      <Card className="overflow-hidden rounded-md border">
        <div className="hidden grid-cols-[60px_1fr_150px_140px_170px] gap-3 border-b border-border bg-muted/50 px-4 py-2.5 sm:grid">
          <Label2>#</Label2><Label2>Ereignis</Label2><Label2>Objekt</Label2><Label2>Akteur</Label2><Label2>Zeitpunkt</Label2>
        </div>
        <div className="divide-y divide-border">
          {entries.map((e) => (
            <div key={e.id} data-testid="audit-row" className="grid grid-cols-1 gap-1 px-4 py-3 sm:grid-cols-[60px_1fr_150px_140px_170px] sm:gap-3">
              <span className="font-mono text-xs text-primary">#{e.seq}</span>
              <span className="font-mono text-sm">{e.event_type}</span>
              <span className="text-sm text-muted-foreground">{e.entity_type}</span>
              <span className="text-sm text-muted-foreground">{e.actor_name}</span>
              <span className="text-xs text-muted-foreground">{new Date(e.timestamp).toLocaleString("de-DE")}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
